function scalarFromZoho(value) {
  if (value == null) return '';
  if (typeof value === 'boolean') return value;
  if (typeof value !== 'object') return value;
  if (value.name != null) return value.name;
  if (value.display_value != null) return value.display_value;
  if (value.email != null) return value.email;
  return String(value);
}

function boolFromZoho(value) {
  if (value === true || value === false) return value;
  if (value == null) return false;
  const s = String(scalarFromZoho(value)).toLowerCase();
  return s === 'true' || s === 'yes';
}

function stringUrl(value) {
  const s = typeof value === 'string' ? value.trim() : '';
  return /^https?:\/\//i.test(s) ? s : '';
}

function fileNameFromUrl(url) {
  try {
    const u = new URL(url);
    const seg = u.pathname.split('/').filter(Boolean).pop() || 'template';
    return decodeURIComponent(seg.split('?')[0]);
  } catch {
    return 'template';
  }
}

function isZipTemplateRef(url, fileName) {
  const name = String(fileName || '').toLowerCase();
  if (name.endsWith('.zip')) return true;
  const u = String(url || '')
    .split(/[?#]/)[0]
    .toLowerCase();
  return u.endsWith('.zip');
}

/** When CRM returns several files, the portal often exposes a single .zip — prefer that URL. */
function pickTemplateFromFileCandidates(candidates) {
  if (!candidates.length) return { url: '', fileName: '' };
  if (candidates.length === 1) return candidates[0];
  const zip = candidates.find(c => isZipTemplateRef(c.url, c.fileName));
  return zip || candidates[0];
}

function parseCcList(value) {
  if (value == null) return [];
  if (Array.isArray(value)) {
    return value.map(v => String(scalarFromZoho(v)).trim()).filter(Boolean);
  }
  const s = String(scalarFromZoho(value)).trim();
  if (!s) return [];
  return s
    .split(/[,;]/)
    .map(x => x.trim())
    .filter(Boolean);
}

function templateFromRecord(record) {
  const urlFieldKeys = [
    'Templates_ZIP_URL',
    'Reconciliation_ZIP_URL',
    'Template_Bundle_URL',
    'Template_Archive_URL',
    'ZIP_URL',
    'Archive_URL',
    'Bundle_URL',
    'Reconciliation_Template_URL',
    'Template_URL',
    'Template_File_URL',
    'Download_URL',
    'File_URL',
    'Portal_Template_URL',
    'Attachment_URL'
  ];
  for (const key of urlFieldKeys) {
    const u = stringUrl(record[key]);
    if (u) return { url: u, fileName: fileNameFromUrl(u) };
  }

  const fileFieldKeys = [
    'Template_File',
    'Reconciliation_Template',
    'Attachment',
    'Attachments',
    'File_Upload',
    'Upload'
  ];
  const fileCandidates = [];
  for (const key of fileFieldKeys) {
    const val = record[key];
    const list = Array.isArray(val) ? val : val != null ? [val] : [];
    for (const item of list) {
      if (!item) continue;
      if (typeof item === 'string') {
        const u = stringUrl(item);
        if (u) fileCandidates.push({ url: u, fileName: fileNameFromUrl(u) });
        continue;
      }
      if (typeof item !== 'object') continue;
      const u = stringUrl(
        item.download_url ||
          item.downloadUrl ||
          item.Url ||
          item.url ||
          item.link_url ||
          item.href ||
          item.Link_URL
      );
      const name =
        item.file_Name ||
        item.File_Name ||
        item.name ||
        (u ? fileNameFromUrl(u) : '');
      if (u)
        fileCandidates.push({
          url: u,
          fileName: name || fileNameFromUrl(u)
        });
    }
  }

  return pickTemplateFromFileCandidates(fileCandidates);
}

function tagTypeFromThrough(throughRaw) {
  const t = String(scalarFromZoho(throughRaw)).toLowerCase();
  if (
    t.includes('sunshine') ||
    t.includes('internal') ||
    t.includes('slh') ||
    t.includes('agency')
  ) {
    return 'internal';
  }
  return 'external';
}

function monogram(record, carrierName) {
  const short = String(scalarFromZoho(record.Short_Carrier_Name) || '').trim();
  if (short.length >= 2 && short.length <= 5) return short.toUpperCase();
  const words = String(carrierName || '')
    .split(/\s+/)
    .filter(Boolean);
  if (words.length >= 2) {
    return `${words[0][0] || ''}${words[1][0] || ''}`.toUpperCase();
  }
  return (
    String(carrierName || '??')
      .replace(/[^a-zA-Z0-9]/g, '')
      .slice(0, 3)
      .toUpperCase() || '??'
  );
}

/**
 * Zoho Reconciliation_Forms — template file/URL fields supported in addition
 * to the core CRM API names you listed.
 */
export function reconciliationFormFactory(record = {}) {
  const id = record.id != null ? String(record.id) : '';
  const carrierName = scalarFromZoho(record.Name);
  const { url: templateUrl, fileName: templateFileName } =
    templateFromRecord(record);
  const hasTemplateFile = Boolean(templateUrl);
  const requiresTemplate = boolFromZoho(record.Requires_Template);
  const isOther = /other\s*carriers/i.test(String(carrierName));
  const isEmailOnly = !hasTemplateFile && !isOther;

  return {
    id,
    active: boolFromZoho(record.Active),
    carrierColor: scalarFromZoho(record.Carrier_Color),
    carrierName,
    region: scalarFromZoho(record.Region || record.Region_Text || ''),
    cc: scalarFromZoho(record.CC),
    ccList: parseCcList(record.CC),
    emailMessage: scalarFromZoho(record.Email_Message),
    emailOptOut: boolFromZoho(record.Email_Opt_Out),
    emailSubject: scalarFromZoho(record.Email_Subject),
    requiresTemplate,
    sendToEmail: scalarFromZoho(record.Email),
    shortCarrierName: scalarFromZoho(record.Short_Carrier_Name),
    through: scalarFromZoho(record.Through),
    tagType: tagTypeFromThrough(record.Through),
    templateUrl,
    templateFileName,
    hasTemplateFile,
    isEmailOnly,
    isOther,
    monogram: monogram(record, carrierName)
  };
}
